import React from "react";
import { Divider, Stack, Typography } from "@mui/material";
import Grid from "@mui/material/Grid";
import FormLegend from "../../Form/Common/FormLegend/FormLegend";
import ValidatorSelectField from "../../FormFields/ValidatorSelectField";
import MenuItem from "@mui/material/MenuItem";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import {
  emailDefault,
  isNotDuplicateMail,
  isPhoneNumberDefault,
  minStringLength,
  postalCodeDefault,
  requiredFieldDefault,
  streetAddressDefault,
  textFieldDefault,
} from "../../../services/validationRules";
import { useForm } from "../../../provider/Form/FormProvider";
import { formValue } from "../../../services/formServiceFunctions";
import _isEmpty from "lodash/isEmpty";
import LoginIndependentFileUpload from "../../Form/Common/FileUpload/LoginIndependentFileUpload";
import ButtonLoading from "../../Button/ButtonLoading";
import { translate } from "../../../services/translations/translations";
import ValidatorMultiSelectFieldWithCheckboxes from "../../FormFields/ValidatorMultiSelectFieldWithCheckboxes";
import LinkLegalbird from "@/src/components/legalbird/LinkLegalbird/LinkLegalbird";
import { OpenInNew } from "@mui/icons-material";

type ExternalLawyerApplicationFormProps = {
  isLoading: boolean;
  lawyerCertificate: File[];
  setLawyerCertificate: (value: File[]) => void;
  specialistLawyerCertificate: File[];
  setSpecialistLawyerCertificate: (value: File[]) => void;
};

export default function ExternalLawyerApplicationForm({
  isLoading,
  lawyerCertificate,
  setLawyerCertificate,
  specialistLawyerCertificate,
  setSpecialistLawyerCertificate,
}: ExternalLawyerApplicationFormProps) {
  const { values, handleSubmit, errors } = useForm();

  const submitDisabled = lawyerCertificate.length < 1 || (formValue(values, "hasSpecialistTitles") === "true" && specialistLawyerCertificate.length < 1);

  return (
    <form onSubmit={handleSubmit}>
      <Typography align={"center"} component={"h3"} variant={"h5"} sx={{ marginBottom: "1rem" }}>
        Jetzt bewerben
      </Typography>
      <Stack
        spacing={6}
        sx={{
          justifyContent: "center",
        }}
      >
        <Grid container spacing={1}>
          <Grid size={12}>
            <FormLegend>Wie heißen Sie?</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorSelectField label="Anrede" name="gender" validators={[requiredFieldDefault]} autoComplete="sex">
              <MenuItem value={"female"}>Frau</MenuItem>
              <MenuItem value={"male"}>Herr</MenuItem>
            </ValidatorSelectField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <ValidatorTextField
              name={`givenName`}
              label="Vorname"
              validators={[
                requiredFieldDefault,
                {
                  validator: minStringLength,
                  params: [2],
                  message: "Bitte kontrollieren Sie Ihre Eingabe",
                },
              ]}
              autoComplete="given-name"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <ValidatorTextField
              name={`familyName`}
              label="Nachname"
              validators={[
                requiredFieldDefault,
                {
                  validator: minStringLength,
                  params: [2],
                  message: "Bitte kontrollieren Sie Ihre Eingabe",
                },
              ]}
              autoComplete="family-name"
            />
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid size={12}>
            <FormLegend>Wo wohnen Sie?</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorTextField
              name={`streetAddress`}
              label="Straße und Hausnummer"
              validators={[requiredFieldDefault, streetAddressDefault]}
              autoComplete="street-address"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <ValidatorTextField name={`postalCode`} label="Postleitzahl" validators={[requiredFieldDefault, postalCodeDefault]} autoComplete="postal-code" />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 6,
            }}
          >
            <ValidatorTextField name={`addressLocality`} label="Ort" validators={[requiredFieldDefault]} autoComplete="address-level2" />
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid size={12}>
            <FormLegend>Wie können wir Sie erreichen?</FormLegend>
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ValidatorTextField
              name="email"
              label="Ihre E-Mail Adresse"
              validators={[
                requiredFieldDefault,
                emailDefault,
                {
                  validator: isNotDuplicateMail,
                  message:
                    "Sie haben schon ein Konto bei uns, oder haben sich bereits beworben? Dann nutzen Sie den Link aus der E-Mail, die Sie von uns erhalten haben, um sich anzumelden.",
                },
              ]}
              autoComplete="email"
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <ValidatorTextField name="telephone" label="Ihre Telefonnummer" validators={[requiredFieldDefault, ...isPhoneNumberDefault]} autoComplete="tel" />
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid size={12}>
            <FormLegend>Wie viele Jahre Berufserfahrung haben Sie?</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorSelectField label="Berufserfahrung (in Jahren)" name="yearsOfExperience" validators={[requiredFieldDefault]}>
              <MenuItem value={"<1"}>weniger als 1 Jahr</MenuItem>
              <MenuItem value={"1-5"}>1 - 5 Jahre</MenuItem>
              <MenuItem value={"5-10"}>5 - 10 Jahre</MenuItem>
              <MenuItem value={">10"}>mehr als 10 Jahre</MenuItem>
            </ValidatorSelectField>
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid size={12}>
            <FormLegend>Haben Sie einen Fachanwaltstitel?</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorSelectField label="Fachanwaltstitel vorhanden" name="hasSpecialistTitles" validators={[requiredFieldDefault]}>
              <MenuItem value={"true"}>Ja</MenuItem>
              <MenuItem value={"false"}>Nein</MenuItem>
            </ValidatorSelectField>
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid size={12}>
            <FormLegend>In welchen Rechtsbereichen können Sie uns unterstützen?</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorMultiSelectFieldWithCheckboxes
              label="Rechtsbereiche"
              name="legalFieldExperience"
              renderValue={(selected) =>
                selected.map((value) => translate(`externalLawyerApplication.legalFieldExperience.options.${value}.label`, values)).join(", ")
              }
              validators={[requiredFieldDefault]}
              helperText={"Sie können mehrere Einträge in der Liste auswählen."}
              options={[
                {
                  value: "contractLaw",
                  label: "Vertragsrecht",
                },
                {
                  value: "familyLaw",
                  label: "Familienrecht",
                },
                {
                  value: "trafficLaw",
                  label: "Verkehrsrecht",
                },
                {
                  value: "employmentLaw",
                  label: "Arbeitsrecht",
                },
                {
                  value: "tenancyLaw",
                  label: "Mietrecht",
                },
                {
                  value: "civilLaw",
                  label: "Zivilrecht",
                },
                {
                  value: "administrativeLaw",
                  label: "Verwaltungsrecht",
                },
              ]}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid size={12}>
            <FormLegend>Wie viele Stunden möchten Sie pro Woche für Legalbird arbeiten?</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorSelectField
              label="Arbeitsstunden pro Woche"
              name="weeklyHours"
              helperText={
                "Sie sollten im Schnitt mindestens 10 Stunden pro Woche Zeit investieren können, damit eine Partnerschaft für beide Seiten Sinn macht."
              }
              validators={[requiredFieldDefault]}
            >
              <MenuItem value={"10"}>10 Stunden</MenuItem>
              <MenuItem value={"15"}>15 Stunden</MenuItem>
              <MenuItem value={"20"}>20 Stunden</MenuItem>
              <MenuItem value={"25"}>25 Stunden</MenuItem>
              <MenuItem value={"30"}>30 Stunden</MenuItem>
              <MenuItem value={"35"}>35 Stunden</MenuItem>
              <MenuItem value={"40"}>40 Stunden</MenuItem>
            </ValidatorSelectField>
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid size={12}>
            <FormLegend>Welche der folgenden Aufgaben könnten Sie übernehmen?</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorMultiSelectFieldWithCheckboxes
              label="Aufgabenart"
              name="taskType"
              helperText={
                "Derzeit werden hauptsächlich Schriftsätze im Vertragsrecht gefertigt. Zukünftig werden weitere Aufgaben folgen. " +
                "Sie können mehrere Einträge in der Liste auswählen."
              }
              validators={[requiredFieldDefault]}
              renderValue={(selected) => selected.map((value) => translate(`externalLawyerApplication.taskType.options.${value}.label`, values)).join(", ")}
              options={[
                {
                  value: "legalDocuments",
                  label: "Schriftsätze fertigen (Außergerichtliche Schreiben, Klageschriften etc.)",
                },
                {
                  value: "checkCases",
                  label: "Ermittlungsakten prüfen (z.B. im Verkehrsrecht)",
                },
                {
                  value: "investigationFiles",
                  label: "Fallprüfung und Ersteinschätzung Vertragsrecht",
                },
              ]}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1}>
          <Grid size={12}>
            <FormLegend>Ihre Referenzen</FormLegend>
          </Grid>
          <Grid size={12}>
            <ValidatorTextField
              name="referenceSites"
              label="Link für Referenz"
              validators={[textFieldDefault]}
              helperText={
                "Sie haben Online-Referenzen oder Bewertungen, wie ein Profil bei Anwalt.de, Google oder eine eigene Website, dann fügen Sie uns gerne den Link hier ein."
              }
            />
          </Grid>
        </Grid>
        <Divider />
        <Grid container spacing={1}>
          <Grid size={12}>
            <LoginIndependentFileUpload
              headline={"Anwaltszulassung"}
              helperText={"Bitte laden Sie hier Ihre Zulassungsurkunde oder Ihr zweites Staatsexamen hoch."}
              files={lawyerCertificate}
              setFiles={setLawyerCertificate}
            />
          </Grid>
        </Grid>
        {formValue(values, "hasSpecialistTitles") === "true" && (
          <LoginIndependentFileUpload
            headline={"Nachweis Fachanwaltstitel"}
            helperText={"Sie haben angegeben, dass Sie einen Fachanwaltstitel besitzen. Bitte laden Sie hierzu einen Nachweis hoch."}
            files={specialistLawyerCertificate}
            setFiles={setSpecialistLawyerCertificate}
          />
        )}
        <Stack>
          <Typography variant={"body2"} align={"center"} sx={{ marginBottom: "3rem" }}>
            Ich stimme dem{" "}
            <LinkLegalbird to={"/datenschutz"} target={"_blank"} standalone>
              Datenschutz <OpenInNew />
            </LinkLegalbird>{" "}
            für die Verarbeitung meiner Bewerbung zu.
          </Typography>
          <ButtonLoading variant={"contained"} isLoading={isLoading} type={"submit"} disabled={submitDisabled} fullWidth>
            Jetzt bewerben
          </ButtonLoading>
          {!_isEmpty(errors) && (
            <Typography variant={"body2"} sx={{ color: "red", textAlign: "center", marginTop: "0.5rem" }}>
              Leider sind Ihre Angaben noch nicht richtig oder vollständig. Bitte prüfen Sie Ihre Angaben oben.
            </Typography>
          )}
        </Stack>
      </Stack>
    </form>
  );
}
